import { hybridStorage, StorageStrategy, DATA_TYPE_CONFIGS } from './HybridStorage';
import { syncQueue } from './SyncQueue';
import { 
  getVehicles, 
  getVehicleById, 
  createVehicle, 
  updateVehicle, 
  deleteVehicle,
  getDrivers,
  getDriverById,
  createDriver,
  updateDriver,
  deleteDriver,
  createAssignmentHistory,
  getAssignmentHistory,
  createVehicleReceivingForm,
  createVehicleDamage,
  getVehicleDamagesByReceivingForm
} from '../../api/supabase';

export interface HybridStoreConfig {
  dataType: string;
  supabaseApi?: {
    create: (data: any) => Promise<any>;
    update: (id: string, data: any) => Promise<any>;
    delete: (id: string) => Promise<void>;
    getAll: () => Promise<any[]>;
    getById: (id: string) => Promise<any>;
  };
}

export interface HybridStoreOperations<T = any> {
  // Core CRUD operations
  create: (data: Partial<T>) => Promise<T>;
  update: (id: string, data: Partial<T>) => Promise<T>;
  delete: (id: string) => Promise<void>;
  getById: (id: string) => Promise<T | null>;
  getAll: () => Promise<T[]>;
  
  // Cache management
  clearCache: () => Promise<void>;
  refreshFromServer: () => Promise<void>;
  
  // Sync operations
  getPendingSync: () => Promise<T[]>;
  forceSyncItem: (id: string) => Promise<void>;
  
  // Utility
  getStats: () => Promise<any>;
}

export function createHybridStore<T = any>(config: HybridStoreConfig): HybridStoreOperations<T> {
  const { dataType, supabaseApi } = config;
  const dataConfig = DATA_TYPE_CONFIGS[dataType];
  
  if (!dataConfig) {
    throw new Error(`Unknown data type: ${dataType}. Please add it to DATA_TYPE_CONFIGS.`);
  }

  // Ensure storage is initialized
  const ensureInitialized = async () => {
    try {
      await hybridStorage.initialize();
    } catch (error) {
      console.warn('⚠️ Storage initialization failed, continuing with limited functionality:', error);
    }
  };

  return {
    async create(data: Partial<T>): Promise<T> {
      const id = (data as any).id || `${dataType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fullData = { ...data, id, created_at: new Date().toISOString() };

      try {
        // Try database first if we have API config
        if (supabaseApi?.create) {
          console.log(`🔄 Attempting to save ${dataType} to database first...`);
          const result = await supabaseApi.create(fullData);
          console.log(`✅ ${dataType} saved to database:`, result.id || id);
          
          // Also store locally for offline access
          await hybridStorage.store(dataType, result, result.id || id);
          return result as T;
        } else {
          // No API config, store locally only
          await hybridStorage.store(dataType, fullData, id);
          console.log(`✅ ${dataType} created locally (no API):`, id);
          return fullData as T;
        }
      } catch (error) {
        console.warn(`⚠️ Database save failed for ${dataType}, falling back to local storage:`, error);
        
        // Fallback to local storage
        try {
          await hybridStorage.store(dataType, fullData, id);
          console.log(`✅ ${dataType} saved locally as fallback:`, id);
          
          // Queue for sync later
          if (supabaseApi?.create) {
            try {
              await syncQueue.addToQueue(
                'create',
                dataType,
                id,
                fullData,
                dataConfig.syncPriority || 3
              );
            } catch (syncError) {
              console.warn(`⚠️ Failed to add creation to sync queue, but local creation succeeded:`, syncError);
            }
          }
          
          return fullData as T;
        } catch (localError) {
          console.error(`❌ Both database and local storage failed for ${dataType}:`, localError);
          throw localError;
        }
      }
    },

    async update(id: string, data: Partial<T>): Promise<T> {
      try {
        console.log(`🔍 Updating ${dataType} ${id} with data:`, data);
        
        // Get existing data
        const existing = await hybridStorage.retrieve(dataType, id);
        if (!existing) {
          throw new Error(`${dataType} with id ${id} not found`);
        }
        
        console.log(`🔍 Existing ${dataType} data:`, existing);

        // Merge existing data with new data
        const updatedData = { 
          ...existing, 
          ...data, 
          id, 
          updated_at: new Date().toISOString() 
        };
        
        // For vehicles, clean up duplicate app/db schema fields after merge
        // The merged data now has BOTH schemas, we need to remove app-schema duplicates
        if (dataType === 'vehicles' && updatedData) {
          // If db-schema field exists, remove the corresponding app-schema field
          if ('license_plate' in updatedData && 'licensePlate' in updatedData) {
            delete updatedData.licensePlate;
          }
          if ('make_model' in updatedData && 'makeModel' in updatedData) {
            delete updatedData.makeModel;
          }
          if ('vin' in updatedData && 'vinNumber' in updatedData) {
            delete updatedData.vinNumber;
          }
          if ('car_status' in updatedData && 'carStatus' in updatedData) {
            delete updatedData.carStatus;
          }
          if ('current_driver_id' in updatedData && 'currentDriverId' in updatedData) {
            delete updatedData.currentDriverId;
          }
          if ('daily_rental_rate' in updatedData) {
            if ('dailyRent' in updatedData) delete updatedData.dailyRent;
            if ('dailyRentalRate' in updatedData) delete updatedData.dailyRentalRate;
          }
          if ('monthly_rental_rate' in updatedData && 'monthlyRentalRate' in updatedData) {
            delete updatedData.monthlyRentalRate;
          }
          if ('insurance_expiry' in updatedData && 'insuranceExpiry' in updatedData) {
            delete updatedData.insuranceExpiry;
          }
          if ('registration_expiry' in updatedData && 'registrationExpiry' in updatedData) {
            delete updatedData.registrationExpiry;
          }
        }
        
        console.log(`🔍 Merged ${dataType} data:`, updatedData);

        // Update locally first
        await hybridStorage.store(dataType, updatedData, id);
        console.log(`✅ ${dataType} updated locally:`, id);

        // Queue for sync
        if (supabaseApi?.update) {
          try {
            await syncQueue.addToQueue(
              'update',
              dataType,
              id,
              updatedData,
              dataConfig.syncPriority || 3
            );
          } catch (syncError) {
            // Don't fail the update if sync queue fails
            console.warn(`⚠️ Failed to add update to sync queue, but local update succeeded:`, syncError);
          }
        }

        return updatedData as T;
      } catch (error) {
        console.error(`❌ Error updating ${dataType}:`, error);
        throw error;
      }
    },

    async delete(id: string): Promise<void> {
      try {
        // For SQLite, mark as deleted
        if (dataConfig.strategy === StorageStrategy.SQLITE || dataConfig.strategy === StorageStrategy.BOTH) {
          await hybridStorage.store(dataType, { 
            id, 
            deleted_at: new Date().toISOString(),
            sync_status: 'pending'
          }, id);
        }

        // For localStorage only, actually remove
        if (dataConfig.strategy === StorageStrategy.LOCAL_STORAGE) {
          // Remove from localStorage (handled internally)
        }

        console.log(`✅ ${dataType} marked for deletion:`, id);

        // Queue for sync
        if (supabaseApi?.delete) {
          try {
            await syncQueue.addToQueue(
              'delete',
              dataType,
              id,
              undefined,
              dataConfig.syncPriority || 3
            );
          } catch (syncError) {
            console.warn(`⚠️ Failed to add deletion to sync queue, but local deletion succeeded:`, syncError);
          }
        }
      } catch (error) {
        console.error(`❌ Error deleting ${dataType}:`, error);
        throw error;
      }
    },

    async getById(id: string): Promise<T | null> {
      try {
        const data = await hybridStorage.retrieve(dataType, id);
        return data as T | null;
      } catch (error) {
        console.error(`❌ Error getting ${dataType} by id:`, error);
        return null;
      }
    },

    async getAll(): Promise<T[]> {
      try {
        await ensureInitialized();
        const data = await hybridStorage.retrieve(dataType);
        return (Array.isArray(data) ? data : []) as T[];
      } catch (error) {
        console.error(`❌ Error getting all ${dataType}:`, error);
        return [];
      }
    },

    async clearCache(): Promise<void> {
      try {
        await hybridStorage.clearCache(dataType);
        console.log(`✅ ${dataType} cache cleared`);
      } catch (error) {
        console.error(`❌ Error clearing ${dataType} cache:`, error);
        throw error;
      }
    },

    async refreshFromServer(): Promise<void> {
      if (!supabaseApi?.getAll) {
        console.warn(`No API configured for ${dataType}, cannot refresh from server`);
        return;
      }

      try {
        console.log(`🔄 Refreshing ${dataType} from server...`);
        const serverData = await supabaseApi.getAll();
        
        // Store each item individually
        for (const item of serverData) {
          await hybridStorage.store(dataType, item, item.id);
        }
        
        // Also store the complete array for getAll() operations
        // This ensures getAll() can retrieve the data efficiently
        const config = hybridStorage.getDataTypeConfig(dataType);
        if (config?.key) {
          await hybridStorage.storeArray(dataType, serverData);
        }

        console.log(`✅ ${dataType} refreshed from server (${serverData.length} items)`);
      } catch (error) {
        console.error(`❌ Error refreshing ${dataType} from server:`, error);
        throw error;
      }
    },

    async getPendingSync(): Promise<T[]> {
      try {
        const allPending = await hybridStorage.getAllPendingSync();
        return (allPending[dataType] || []) as T[];
      } catch (error) {
        console.error(`❌ Error getting pending sync for ${dataType}:`, error);
        return [];
      }
    },

    async forceSyncItem(id: string): Promise<void> {
      if (!supabaseApi) {
        console.warn(`No API configured for ${dataType}, cannot sync`);
        return;
      }

      try {
        const item = await hybridStorage.retrieve(dataType, id);
        if (!item) {
          throw new Error(`${dataType} with id ${id} not found`);
        }

        // Add high priority sync job
        try {
          await syncQueue.addToQueue(
            'update', // Default to update
            dataType,
            id,
            item,
            5 // Highest priority
          );
        } catch (syncError) {
          console.warn(`⚠️ Failed to force sync item ${id}:`, syncError);
          throw syncError;
        }

        console.log(`✅ ${dataType} queued for immediate sync:`, id);
      } catch (error) {
        console.error(`❌ Error forcing sync for ${dataType}:`, error);
        throw error;
      }
    },

    async getStats(): Promise<any> {
      try {
        const allStats = await hybridStorage.getStorageStats();
        const pending = await this.getPendingSync();
        
        return {
          totalItems: dataConfig.strategy === StorageStrategy.LOCAL_STORAGE 
            ? allStats.localStorage[dataType] || 0
            : allStats.sqlite[dataType] || 0,
          pendingSync: pending.length,
          strategy: dataConfig.strategy,
          cacheDuration: dataConfig.cacheDuration,
          lastAccessed: new Date().toISOString()
        };
      } catch (error) {
        console.error(`❌ Error getting stats for ${dataType}:`, error);
        return { error: error instanceof Error ? error.message : String(error) };
      }
    }
  };
}

// Pre-configured hybrid stores for common data types
export const vehicleHybridStore = createHybridStore({ 
  dataType: 'vehicles',
  supabaseApi: {
    create: async (data) => {
      try {
        return await createVehicle(data);
      } catch (error) {
        console.warn('🔄 Vehicle create failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id, data) => {
      try {
        return await updateVehicle(id, data);
      } catch (error) {
        console.warn('🔄 Vehicle update failed, storing offline:', error);
        throw error;
      }
    },
    delete: async (id) => {
      try {
        await deleteVehicle(id);
      } catch (error) {
        console.warn('🔄 Vehicle delete failed, storing offline:', error);
        throw error;
      }
    },
    getAll: async () => {
      try {
        return await getVehicles();
      } catch (error) {
        console.warn('🔄 Vehicle getAll failed, using cache:', error);
        throw error;
      }
    },
    getById: async (id) => {
      try {
        return await getVehicleById(id);
      } catch (error) {
        console.warn('🔄 Vehicle getById failed, using cache:', error);
        throw error;
      }
    }
  }
});

export const driverHybridStore = createHybridStore({ 
  dataType: 'drivers',
  supabaseApi: {
    create: async (data) => {
      try {
        return await createDriver(data);
      } catch (error) {
        console.warn('🔄 Driver create failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id, data) => {
      try {
        return await updateDriver(id, data);
      } catch (error) {
        console.warn('🔄 Driver update failed, storing offline:', error);
        throw error;
      }
    },
    delete: async (id) => {
      try {
        await deleteDriver(id);
      } catch (error) {
        console.warn('🔄 Driver delete failed, storing offline:', error);
        throw error;
      }
    },
    getAll: async () => {
      try {
        return await getDrivers();
      } catch (error) {
        console.warn('🔄 Driver getAll failed, using cache:', error);
        throw error;
      }
    },
    getById: async (id) => {
      try {
        return await getDriverById(id);
      } catch (error) {
        console.warn('🔄 Driver getById failed, using cache:', error);
        throw error;
      }
    }
  }
});

export const assignmentHybridStore = createHybridStore({ 
  dataType: 'assignment_history',
  supabaseApi: {
    create: async (data) => {
      try {
        console.log('🔄 Attempting to save assignment to database...', data);
        return await createAssignmentHistory(data);
      } catch (error) {
        console.warn('🔄 Assignment database save failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id: string, data: any) => {
      throw new Error('Assignment update not implemented');
    },
    delete: async (id: string) => {
      throw new Error('Assignment delete not implemented');
    },
    getAll: async () => {
      try {
        return await getAssignmentHistory();
      } catch (error) {
        console.warn('🔄 Assignment database fetch failed:', error);
        throw error;
      }
    },
    getById: async (id: string) => {
      throw new Error('Assignment getById not implemented');
    }
  }
});

export const receivingFormHybridStore = createHybridStore({ 
  dataType: 'receivingForms',
  supabaseApi: {
    create: async (data) => {
      try {
        console.log('🔄 Attempting to save receiving form to database...', data);
        return await createVehicleReceivingForm(data);
      } catch (error) {
        console.warn('🔄 Receiving form database save failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id: string, data: any) => {
      throw new Error('Receiving form update not implemented');
    },
    delete: async (id: string) => {
      throw new Error('Receiving form delete not implemented');
    },
    getAll: async () => {
      throw new Error('Receiving form getAll not implemented');
    },
    getById: async (id: string) => {
      throw new Error('Receiving form getById not implemented');
    }
  }
});

export const deliveryFormHybridStore = createHybridStore({ 
  dataType: 'deliveryForms',
  supabaseApi: {
    create: async (data) => {
      try {
        // PRIORITY: Always try database first
        // Only fall back to local storage on actual connection errors
        const { createVehicleDelivery } = await import('../../api/supabase');
        return await createVehicleDelivery(data);
      } catch (error) {
        console.warn('🔄 Delivery form create failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id, data) => {
      try {
        // For now, delivery form updates are not implemented in the API
        throw new Error('Delivery form updates not supported yet');
      } catch (error) {
        console.warn('🔄 Delivery form update failed, storing offline:', error);
        throw error;
      }
    },
    delete: async (id) => {
      throw new Error('Delivery form delete not implemented');
    },
    getAll: async () => {
      throw new Error('Delivery form getAll not implemented');
    },
    getById: async (id: string) => {
      throw new Error('Delivery form getById not implemented');
    }
  }
});

export const damageHybridStore = createHybridStore({ 
  dataType: 'damages',
  supabaseApi: {
    create: async (data) => {
      try {
        // PRIORITY: Always try database first
        // If parent form has local ID, the database will reject it with foreign key error
        // which will naturally trigger the fallback to local storage
        return await createVehicleDamage(data);
      } catch (error) {
        console.warn('🔄 Damage create failed, storing offline:', error);
        throw error;
      }
    },
    update: async (id, data) => {
      try {
        // For now, damage updates are not implemented in the API
        throw new Error('Damage update not implemented');
      } catch (error) {
        console.warn('🔄 Damage update failed, storing offline:', error);
        throw error;
      }
    },
    delete: async (id) => {
      try {
        // For now, damage deletes are not implemented in the API
        throw new Error('Damage delete not implemented');
      } catch (error) {
        console.warn('🔄 Damage delete failed, storing offline:', error);
        throw error;
      }
    },
    getAll: async () => {
      try {
        // Note: This could be expensive, might want to implement a specific API endpoint
        console.warn('⚠️ Getting all damages - this may be expensive');
        return [];
      } catch (error) {
        console.warn('🔄 Damage getAll failed, using cache:', error);
        throw error;
      }
    },
    getById: async (id) => {
      try {
        // Implement if needed - could query by receiving form ID
        throw new Error('Damage getById not implemented - use getVehicleDamagesByReceivingForm instead');
      } catch (error) {
        console.warn('🔄 Damage getById failed, using cache:', error);
        throw error;
      }
    }
  }
});

// Simple stores for settings and preferences
export const userPreferencesStore = createHybridStore({ 
  dataType: 'userPreferences'
});

export const appSettingsStore = createHybridStore({ 
  dataType: 'appSettings'
});

export const tempFormDataStore = createHybridStore({ 
  dataType: 'tempFormData'
});